// ---------------------------------------------------------------------------
// Glimt — legal content (app / memorial service only)
//
// SCOPE: This app is the memorial service where memorials are set up, viewed
// and moderated. PURCHASES of signs and subscriptions happen on the separate
// shop at https://www.glimtshop.de/ and are governed by THAT site's own Terms
// & Conditions, withdrawal policy, payment and shipping terms. The documents
// here intentionally do NOT cover commerce/billing/returns.
//
// IMPORTANT — NOT LEGAL ADVICE:
// These texts are practical templates based on GDPR + German law (DDG/TTDSG).
// They MUST be reviewed by a qualified German/EU lawyer and all [PLACEHOLDER]
// fields filled in with the registered company details before launch.
// ---------------------------------------------------------------------------

export const LEGAL_VERSION = "1.0";
export const LEGAL_LAST_UPDATED = "2026-06-22";

// Central company details. Fill these in once the entity is registered.
// A German commercial website legally requires these in the Impressum (§5 DDG).
export const COMPANY = {
  tradeName: "Glimt",
  legalName: "[COMPANY LEGAL NAME — e.g. Glimt UG (haftungsbeschränkt) / GmbH]",
  representative: "[REPRESENTATIVE / MANAGING DIRECTOR — e.g. … Christensen]",
  street: "[STREET ADDRESS]",
  postalCity: "[POSTAL CODE + CITY]",
  country: "[COUNTRY — e.g. Germany]",
  email: "[CONTACT EMAIL — e.g. hello@glimt.dk]",
  phone: "[PHONE — optional but recommended]",
  registerCourt: "[REGISTER COURT — e.g. Amtsgericht …]",
  registerNumber: "[REGISTER NUMBER — e.g. HRB …]",
  vatId: "[VAT ID — USt-IdNr. e.g. DE… (omit if Kleinunternehmer §19 UStG)]",
  shopUrl: "https://www.glimtshop.de/",
  appUrl: "[APP URL — e.g. https://app.glimt.dk]",
  dpoEmail: "[DATA PROTECTION CONTACT EMAIL — may be same as contact email]",
};

// The four documents this app exposes. `order` controls footer/nav order.
export const LEGAL_DOCS = ["terms", "privacy", "cookies", "impressum"];

export const LEGAL_TITLES = {
  en: {
    terms: "Terms of Use",
    privacy: "Privacy Policy",
    cookies: "Cookie Notice",
    impressum: "Legal Notice (Impressum)",
  },
  de: {
    terms: "Nutzungsbedingungen",
    privacy: "Datenschutzerklärung",
    cookies: "Cookie-Hinweis",
    impressum: "Impressum",
  },
};

// Section block format:
//   { h: "Heading", body: [ "paragraph", ["bullet", "bullet"], "paragraph" ] }
// A string => paragraph. An array => bullet list.

const C = COMPANY;

export const LEGAL_CONTENT = {
  // ===================================================================== TERMS
  terms: {
    en: {
      intro: `These Terms of Use govern your use of the ${C.tradeName} memorial service (the "Service") — the web app where memorial pages are created, viewed and managed. Buying a sign or subscription happens on our separate shop at ${C.shopUrl} and is governed by that site's own Terms & Conditions, not by this document.`,
      sections: [
        {
          h: "1. Who we are",
          body: [
            `The Service is operated by ${C.legalName}, ${C.street}, ${C.postalCity}, ${C.country} ("we", "us"). You can reach us at ${C.email}. Full provider details are in our Legal Notice (Impressum).`,
          ],
        },
        {
          h: "2. The Service",
          body: [
            `${C.tradeName} lets a memorial owner publish a memorial page for a deceased person and lets visitors view it and submit photos and memory notes ("Contributions"). The memorial owner moderates Contributions before they are published.`,
            `Access to a memorial is via a unique link or QR code. Memorial pages are, by design, publicly viewable by anyone who has the link.`,
          ],
        },
        {
          h: "3. Accounts and eligibility",
          body: [
            "You must be at least 16 years old to create an owner account. You are responsible for keeping your login credentials confidential and for all activity under your account.",
            "Visitors may submit Contributions without a full account; a temporary anonymous session is used to operate the upload feature.",
          ],
        },
        {
          h: "4. Your content and the rights you grant",
          body: [
            "You retain ownership of the photos, text and other material you upload. By uploading, you grant us a worldwide, royalty-free licence to host, store, display and reproduce that material solely for the purpose of operating and providing the Service.",
            "You represent and warrant that, for everything you upload, you either own the rights or have obtained all necessary permissions — including from people depicted or identifiable in photos, and from the holders of any applicable rights of the deceased and their relatives.",
          ],
        },
        {
          h: "5. Acceptable use",
          body: [
            "You agree not to upload or submit content that:",
            [
              "is unlawful, defamatory, harassing, hateful, or violates the dignity of the deceased or any living person;",
              "infringes intellectual property, privacy or personality rights;",
              "contains malware, or attempts to gain unauthorised access to the Service or other users' data;",
              "is commercial spam or unrelated advertising.",
            ],
            "We may remove content and suspend access where we reasonably believe these Terms have been breached.",
          ],
        },
        {
          h: "6. Memorial owner responsibilities (moderation)",
          body: [
            "The memorial owner decides which Contributions are published and is the controller for the personal data they choose to publish on their memorial page. Owners must handle Contributions and the memory of the deceased respectfully and lawfully, and must respond to reasonable take-down requests from affected persons.",
          ],
        },
        {
          h: "7. Purchases, signs and subscriptions",
          body: [
            `Signs and subscriptions are sold and managed on ${C.shopUrl}. All purchase, payment, billing, delivery, cancellation and statutory right-of-withdrawal terms are set out there. This Service does not process payments.`,
          ],
        },
        {
          h: "8. Availability and changes",
          body: [
            "We aim to keep the Service available but do not guarantee uninterrupted operation. We may modify or discontinue features, and may update these Terms; material changes will be communicated by reasonable means and take effect for continued use.",
          ],
        },
        {
          h: "9. Liability",
          body: [
            "Nothing in these Terms limits liability that cannot be limited by law (including for injury to life, body or health, or under mandatory statutory liability). Subject to that, we are liable without limitation for intent and gross negligence; for slight negligence we are liable only for breach of an essential contractual obligation, and limited to the foreseeable, typical damage.",
            "We are not responsible for Contributions submitted by visitors or for content published by memorial owners.",
          ],
        },
        {
          h: "10. Governing law and contact",
          body: [
            `These Terms are governed by the laws of ${C.country}, without prejudice to mandatory consumer-protection provisions of your country of residence. Questions: ${C.email}.`,
          ],
        },
      ],
    },
    de: {
      intro: `Diese Nutzungsbedingungen regeln die Nutzung des Gedenk-Dienstes ${C.tradeName} (der „Dienst") — die Web-App, in der Gedenkseiten erstellt, angesehen und verwaltet werden. Der Kauf eines Schildes oder Abonnements erfolgt in unserem separaten Shop unter ${C.shopUrl} und unterliegt den dortigen Allgemeinen Geschäftsbedingungen, nicht diesem Dokument.`,
      sections: [
        {
          h: "1. Wer wir sind",
          body: [
            `Der Dienst wird betrieben von ${C.legalName}, ${C.street}, ${C.postalCity}, ${C.country} („wir", „uns"). Sie erreichen uns unter ${C.email}. Vollständige Anbieterangaben finden Sie im Impressum.`,
          ],
        },
        {
          h: "2. Der Dienst",
          body: [
            `${C.tradeName} ermöglicht es einem Gedenkseiten-Inhaber, eine Gedenkseite für eine verstorbene Person zu veröffentlichen, und erlaubt Besuchern, diese anzusehen sowie Fotos und Erinnerungstexte einzureichen („Beiträge"). Der Inhaber moderiert Beiträge vor der Veröffentlichung.`,
            `Der Zugang zu einer Gedenkseite erfolgt über einen eindeutigen Link oder QR-Code. Gedenkseiten sind konzeptbedingt für jede Person mit dem Link öffentlich einsehbar.`,
          ],
        },
        {
          h: "3. Konten und Voraussetzungen",
          body: [
            "Zur Erstellung eines Inhaber-Kontos müssen Sie mindestens 16 Jahre alt sein. Sie sind für die Vertraulichkeit Ihrer Zugangsdaten und für alle Aktivitäten unter Ihrem Konto verantwortlich.",
            "Besucher können Beiträge ohne vollständiges Konto einreichen; zur Bereitstellung der Upload-Funktion wird eine temporäre anonyme Sitzung verwendet.",
          ],
        },
        {
          h: "4. Ihre Inhalte und eingeräumte Rechte",
          body: [
            "Die Eigentums- und Urheberrechte an den von Ihnen hochgeladenen Fotos, Texten und sonstigen Inhalten verbleiben bei Ihnen. Mit dem Hochladen räumen Sie uns ein weltweites, unentgeltliches Recht ein, diese Inhalte ausschließlich zum Betrieb und zur Bereitstellung des Dienstes zu hosten, zu speichern, anzuzeigen und zu vervielfältigen.",
            "Sie sichern zu, dass Sie für alle hochgeladenen Inhalte die erforderlichen Rechte besitzen oder alle notwendigen Einwilligungen eingeholt haben — einschließlich der auf Fotos abgebildeten oder identifizierbaren Personen sowie etwaiger Rechte der verstorbenen Person und ihrer Angehörigen.",
          ],
        },
        {
          h: "5. Zulässige Nutzung",
          body: [
            "Sie verpflichten sich, keine Inhalte hochzuladen oder einzureichen, die:",
            [
              "rechtswidrig, verleumderisch, belästigend, hasserfüllt sind oder die Würde der verstorbenen Person oder einer lebenden Person verletzen;",
              "Rechte des geistigen Eigentums, Datenschutz- oder Persönlichkeitsrechte verletzen;",
              "Schadsoftware enthalten oder versuchen, unbefugt auf den Dienst oder Daten anderer Nutzer zuzugreifen;",
              "kommerzieller Spam oder themenfremde Werbung sind.",
            ],
            "Wir können Inhalte entfernen und den Zugang sperren, wenn wir berechtigterweise von einem Verstoß gegen diese Bedingungen ausgehen.",
          ],
        },
        {
          h: "6. Pflichten des Inhabers (Moderation)",
          body: [
            "Der Inhaber entscheidet, welche Beiträge veröffentlicht werden, und ist für die von ihm auf der Gedenkseite veröffentlichten personenbezogenen Daten verantwortlich. Inhaber müssen mit Beiträgen und dem Andenken der verstorbenen Person respektvoll und rechtmäßig umgehen und auf berechtigte Lösch-Anfragen betroffener Personen reagieren.",
          ],
        },
        {
          h: "7. Käufe, Schilder und Abonnements",
          body: [
            `Schilder und Abonnements werden über ${C.shopUrl} verkauft und verwaltet. Alle Bedingungen zu Kauf, Zahlung, Abrechnung, Lieferung, Kündigung und gesetzlichem Widerrufsrecht sind dort geregelt. Dieser Dienst wickelt keine Zahlungen ab.`,
          ],
        },
        {
          h: "8. Verfügbarkeit und Änderungen",
          body: [
            "Wir bemühen uns um die Verfügbarkeit des Dienstes, garantieren jedoch keinen unterbrechungsfreien Betrieb. Wir können Funktionen ändern oder einstellen und diese Bedingungen aktualisieren; über wesentliche Änderungen informieren wir in angemessener Weise.",
          ],
        },
        {
          h: "9. Haftung",
          body: [
            "Die Haftung für Schäden aus der Verletzung des Lebens, des Körpers oder der Gesundheit sowie nach zwingendem Recht bleibt unberührt. Im Übrigen haften wir unbeschränkt für Vorsatz und grobe Fahrlässigkeit; bei einfacher Fahrlässigkeit haften wir nur bei Verletzung einer wesentlichen Vertragspflicht (Kardinalpflicht) und begrenzt auf den vorhersehbaren, typischerweise eintretenden Schaden.",
            "Für von Besuchern eingereichte Beiträge oder von Inhabern veröffentlichte Inhalte sind wir nicht verantwortlich.",
          ],
        },
        {
          h: "10. Anwendbares Recht und Kontakt",
          body: [
            `Es gilt das Recht von ${C.country} unter Wahrung zwingender Verbraucherschutzvorschriften Ihres Wohnsitzstaates. Fragen: ${C.email}.`,
          ],
        },
      ],
    },
  },

  // =================================================================== PRIVACY
  privacy: {
    en: {
      intro: `This Privacy Policy explains how the ${C.tradeName} memorial service processes personal data. The separate shop at ${C.shopUrl} has its own privacy policy for purchases.`,
      sections: [
        {
          h: "1. Controller",
          body: [
            `Controller within the meaning of the GDPR is ${C.legalName}, ${C.street}, ${C.postalCity}, ${C.country}. Data protection contact: ${C.dpoEmail}.`,
          ],
        },
        {
          h: "2. What we process and why",
          body: [
            "Account data (memorial owners): email address and authentication data — to create and secure your account. Legal basis: performance of a contract (Art. 6(1)(b) GDPR).",
            "Memorial content: the deceased person's name and birth/death years, plus photos and memory notes uploaded by owners and visitors. This may include personal data of the deceased and of living contributors. Legal basis: contract and legitimate interests in providing a memorial service (Art. 6(1)(b) and (f) GDPR), and consent where you actively submit a Contribution (Art. 6(1)(a)).",
            "Contributor data: the author name a visitor optionally provides with a Contribution, and a temporary anonymous identifier used to operate uploads and moderation.",
            "Technical data: server and security logs (e.g. IP address, timestamps) needed to operate and secure the Service. Legal basis: legitimate interests (Art. 6(1)(f)).",
          ],
        },
        {
          h: "3. Public visibility of memorials",
          body: [
            "Approved memorial pages are publicly accessible to anyone with the link or QR code. Do not upload information you do not want to be publicly visible. Pending (unapproved) Contributions are not public and are only visible to the memorial owner.",
          ],
        },
        {
          h: "4. Processors and international transfers",
          body: [
            "We host the Service on Google Firebase (Google Ireland Ltd. / Google LLC), which provides database, file storage, authentication and serverless functions. Google acts as our processor under a data processing agreement. Some processing may involve transfers outside the EU/EEA, safeguarded by the EU Standard Contractual Clauses and the EU–US Data Privacy Framework where applicable.",
          ],
        },
        {
          h: "5. Retention",
          body: [
            "Memorial content is retained for as long as the memorial is active. Account data is retained for the life of the account. We delete or anonymise personal data when it is no longer needed or upon a valid deletion request, subject to statutory retention duties.",
          ],
        },
        {
          h: "6. Your rights",
          body: [
            "Under the GDPR you have the right to access, rectification, erasure, restriction, data portability, and objection, and the right to withdraw consent at any time with future effect.",
            `To exercise these rights, contact ${C.dpoEmail}. You also have the right to lodge a complaint with a supervisory authority in your country of residence.`,
          ],
        },
        {
          h: "7. Cookies and local storage",
          body: [
            "The Service uses only storage that is strictly necessary to operate (e.g. to keep you signed in and remember your choices). See our Cookie Notice for details.",
          ],
        },
        {
          h: "8. Changes",
          body: [
            "We may update this Privacy Policy. The current version is always available in the app.",
          ],
        },
      ],
    },
    de: {
      intro: `Diese Datenschutzerklärung erläutert, wie der Gedenk-Dienst ${C.tradeName} personenbezogene Daten verarbeitet. Der separate Shop unter ${C.shopUrl} hat eine eigene Datenschutzerklärung für Käufe.`,
      sections: [
        {
          h: "1. Verantwortlicher",
          body: [
            `Verantwortlicher im Sinne der DSGVO ist ${C.legalName}, ${C.street}, ${C.postalCity}, ${C.country}. Datenschutz-Kontakt: ${C.dpoEmail}.`,
          ],
        },
        {
          h: "2. Was wir verarbeiten und warum",
          body: [
            "Kontodaten (Inhaber): E-Mail-Adresse und Authentifizierungsdaten — zur Erstellung und Absicherung Ihres Kontos. Rechtsgrundlage: Vertragserfüllung (Art. 6 Abs. 1 lit. b DSGVO).",
            "Gedenkinhalte: Name sowie Geburts-/Sterbejahr der verstorbenen Person sowie von Inhabern und Besuchern hochgeladene Fotos und Erinnerungstexte. Dies kann personenbezogene Daten der verstorbenen Person und lebender Beitragender umfassen. Rechtsgrundlage: Vertrag und berechtigte Interessen an der Bereitstellung eines Gedenk-Dienstes (Art. 6 Abs. 1 lit. b und f DSGVO) sowie Einwilligung beim aktiven Einreichen eines Beitrags (Art. 6 Abs. 1 lit. a).",
            "Beitragenden-Daten: der optional angegebene Autorenname zu einem Beitrag sowie eine temporäre anonyme Kennung zum Betrieb von Upload und Moderation.",
            "Technische Daten: Server- und Sicherheits-Logs (z. B. IP-Adresse, Zeitstempel) zum Betrieb und zur Absicherung des Dienstes. Rechtsgrundlage: berechtigte Interessen (Art. 6 Abs. 1 lit. f).",
          ],
        },
        {
          h: "3. Öffentliche Sichtbarkeit von Gedenkseiten",
          body: [
            "Freigegebene Gedenkseiten sind für jede Person mit dem Link oder QR-Code öffentlich zugänglich. Laden Sie keine Informationen hoch, die nicht öffentlich sichtbar sein sollen. Ausstehende (nicht freigegebene) Beiträge sind nicht öffentlich und nur für den Inhaber sichtbar.",
          ],
        },
        {
          h: "4. Auftragsverarbeiter und Drittlandübermittlung",
          body: [
            "Wir hosten den Dienst bei Google Firebase (Google Ireland Ltd. / Google LLC), das Datenbank, Dateispeicher, Authentifizierung und serverlose Funktionen bereitstellt. Google handelt als Auftragsverarbeiter auf Grundlage eines Auftragsverarbeitungsvertrags. Verarbeitungen können Übermittlungen außerhalb der EU/des EWR umfassen, abgesichert durch die EU-Standardvertragsklauseln und ggf. das EU-US Data Privacy Framework.",
          ],
        },
        {
          h: "5. Speicherdauer",
          body: [
            "Gedenkinhalte werden gespeichert, solange die Gedenkseite aktiv ist. Kontodaten werden für die Dauer des Kontos gespeichert. Wir löschen oder anonymisieren personenbezogene Daten, sobald sie nicht mehr erforderlich sind oder auf ein berechtigtes Löschverlangen hin, vorbehaltlich gesetzlicher Aufbewahrungspflichten.",
          ],
        },
        {
          h: "6. Ihre Rechte",
          body: [
            "Nach der DSGVO haben Sie das Recht auf Auskunft, Berichtigung, Löschung, Einschränkung, Datenübertragbarkeit und Widerspruch sowie das Recht, eine Einwilligung jederzeit mit Wirkung für die Zukunft zu widerrufen.",
            `Zur Ausübung dieser Rechte wenden Sie sich an ${C.dpoEmail}. Ihnen steht zudem ein Beschwerderecht bei einer Aufsichtsbehörde zu.`,
          ],
        },
        {
          h: "7. Cookies und lokaler Speicher",
          body: [
            "Der Dienst verwendet ausschließlich Speicher, der für den Betrieb unbedingt erforderlich ist (z. B. um Sie angemeldet zu halten und Ihre Auswahl zu speichern). Einzelheiten finden Sie im Cookie-Hinweis.",
          ],
        },
        {
          h: "8. Änderungen",
          body: [
            "Wir können diese Datenschutzerklärung aktualisieren. Die jeweils aktuelle Fassung ist stets in der App verfügbar.",
          ],
        },
      ],
    },
  },

  // =================================================================== COOKIES
  cookies: {
    en: {
      intro: `This notice explains the cookies and similar local storage used by the ${C.tradeName} memorial app.`,
      sections: [
        {
          h: "1. Strictly necessary storage",
          body: [
            "The app uses only strictly necessary browser storage to function. No consent is required for these under the GDPR/TTDSG, but we disclose them for transparency:",
            [
              "Authentication: Firebase Authentication keeps you signed in (including a temporary anonymous session that powers the upload feature).",
              "Preferences: small local-storage entries remember your current memorial, your cookie choice, and admin-mode UI state.",
            ],
          ],
        },
        {
          h: "2. No analytics or advertising cookies",
          body: [
            "We currently do not load analytics, tracking or advertising cookies in this app. If we introduce analytics in the future, we will ask for your consent first and update this notice.",
          ],
        },
        {
          h: "3. Managing storage",
          body: [
            "You can clear cookies and local storage at any time in your browser settings. Doing so will sign you out and reset your preferences.",
          ],
        },
      ],
    },
    de: {
      intro: `Dieser Hinweis erläutert die Cookies und ähnlichen lokalen Speicher, die in der ${C.tradeName}-Gedenk-App verwendet werden.`,
      sections: [
        {
          h: "1. Unbedingt erforderlicher Speicher",
          body: [
            "Die App verwendet ausschließlich unbedingt erforderlichen Browser-Speicher. Hierfür ist nach DSGVO/TTDSG keine Einwilligung erforderlich; aus Transparenzgründen legen wir sie offen:",
            [
              "Authentifizierung: Firebase Authentication hält Sie angemeldet (einschließlich einer temporären anonymen Sitzung für die Upload-Funktion).",
              "Einstellungen: kleine Local-Storage-Einträge merken sich Ihre aktuelle Gedenkseite, Ihre Cookie-Auswahl und den Zustand des Admin-Modus.",
            ],
          ],
        },
        {
          h: "2. Keine Analyse- oder Werbe-Cookies",
          body: [
            "Wir laden in dieser App derzeit keine Analyse-, Tracking- oder Werbe-Cookies. Sollten wir künftig Analyse einsetzen, holen wir zuvor Ihre Einwilligung ein und aktualisieren diesen Hinweis.",
          ],
        },
        {
          h: "3. Speicher verwalten",
          body: [
            "Sie können Cookies und lokalen Speicher jederzeit in Ihren Browser-Einstellungen löschen. Dadurch werden Sie abgemeldet und Ihre Einstellungen zurückgesetzt.",
          ],
        },
      ],
    },
  },

  // ================================================================= IMPRESSUM
  impressum: {
    en: {
      intro: "Information pursuant to § 5 DDG (German Digital Services Act).",
      sections: [
        {
          h: "Provider",
          body: [
            `${C.legalName}`,
            `${C.street}`,
            `${C.postalCity}`,
            `${C.country}`,
          ],
        },
        {
          h: "Represented by",
          body: [`${C.representative}`],
        },
        {
          h: "Contact",
          body: [`Email: ${C.email}`, `Phone: ${C.phone}`],
        },
        {
          h: "Register entry",
          body: [
            `Register court: ${C.registerCourt}`,
            `Register number: ${C.registerNumber}`,
          ],
        },
        {
          h: "VAT identification number",
          body: [`${C.vatId}`],
        },
        {
          h: "Responsible for content",
          body: [`${C.representative}, address as above.`],
        },
        {
          h: "Online dispute resolution",
          body: [
            "The European Commission provides a platform for online dispute resolution (ODR): https://ec.europa.eu/consumers/odr. We are neither obliged nor willing to participate in dispute resolution proceedings before a consumer arbitration board.",
          ],
        },
      ],
    },
    de: {
      intro: "Angaben gemäß § 5 DDG (Digitale-Dienste-Gesetz).",
      sections: [
        {
          h: "Anbieter",
          body: [
            `${C.legalName}`,
            `${C.street}`,
            `${C.postalCity}`,
            `${C.country}`,
          ],
        },
        {
          h: "Vertreten durch",
          body: [`${C.representative}`],
        },
        {
          h: "Kontakt",
          body: [`E-Mail: ${C.email}`, `Telefon: ${C.phone}`],
        },
        {
          h: "Registereintrag",
          body: [
            `Registergericht: ${C.registerCourt}`,
            `Registernummer: ${C.registerNumber}`,
          ],
        },
        {
          h: "Umsatzsteuer-Identifikationsnummer",
          body: [`${C.vatId}`],
        },
        {
          h: "Verantwortlich für den Inhalt",
          body: [`${C.representative}, Anschrift wie oben.`],
        },
        {
          h: "Online-Streitbeilegung",
          body: [
            "Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit: https://ec.europa.eu/consumers/odr. Wir sind nicht verpflichtet und nicht bereit, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.",
          ],
        },
      ],
    },
  },
};
